/* eslint-disable react-hooks/exhaustive-deps */
import { Field, Modal, TextField, ActionButton, Autocomplete, Checkbox, Text } from 'components/commons';
import { useApi, useForm, useModal } from 'hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { initialState as formState } from './account-form.state';
import styles from './account.module.scss';
import { createAccount, getAccountByAccountId, updateAccount } from 'apis/account.api';
import { NotifModal } from 'components/modals';
import { Grid } from '@material-ui/core';
import locale from 'localization';
import { CarWithLady } from 'images';
import { handleRequest } from 'utils';
import { SelectCategory } from 'components/field';
import SelectAccount from 'components/field/select-account/select-account';
import { orderBy } from 'lodash';

const AccountForm = ({ initialState, actionText, create, accountId, parentAccountId, account, refreshPage, ...state }) => {
  const { active, close } = state;

  const notifModal = useModal();
  const [nextLevelParentAccount, setNextLevelParentAccount] = useState(null);
  const [parentAccount, setParentAccount] = useState(null);
  const [accountCategory, setAccountCategory] = useState(null);
  const form = useMemo(() => {
    return formState(initialState);
  }, [initialState]);

  const {
    fields,
    modifyField,
    submitForm,
    getFormValues,
    validateField,
    modifyForm,
    isFormSubmittable,
    makeFormInvalid,
    applyFieldErrors
  } = useForm({
    initialState: form
  });

  const addAccountRequest = useApi({
    api: createAccount,
    handleOwnError: {
      badrequest: true
    }
  });

  const editAccountRequest = useApi({
    api: updateAccount,
    handleOwnError: {
      badrequest: true
    },
    params: {
      accountId
    }
  });

  const getAccountRequest = useApi({
    api: getAccountByAccountId,
    handleOwnError: {
      badrequest: true
    },
    params: {
      accountId: fields.parentAccountId.value
    }
  });

  const getHierarchy = (paramAccountId) => {
    handleRequest(async () => {
      const paramsAccountId = paramAccountId;

      const res = await getAccountRequest.request({ accountId: paramsAccountId });

      if (res) {
        const parentAccounts = res.parentAccount;

        setAccountCategory({
          level: 1,
          accountId: res.accountCategoryId,
          code: res.accountCategory?.code || '',
          name: res.accountCategory?.name || ''
        });

        setParentAccount(parentAccounts);
        setNextLevelParentAccount(res);
      }
    });
  };

  useEffect(() => {
    if (active) {
      if (!create) {
        getHierarchy(parentAccountId);
      }
    } else {
      setParentAccount(null);
      setNextLevelParentAccount(null);
    }
  }, [active]);

  const loading = addAccountRequest.loading || editAccountRequest.loading;

  const submit = async () => {
    const params = getFormValues();

    const apiRequest = create ? addAccountRequest : editAccountRequest;

    handleRequest(
      async () => {
        const finalParams = {
          ...params
        };

        if (finalParams.level) {
          finalParams.level = parseInt(finalParams.level);
        } else {
          delete finalParams.level;
        }

        if (finalParams.parentAccountId) {
          finalParams.parentAccountId = parseInt(finalParams.parentAccountId);
        } else {
          delete finalParams.parentAccountId;
        }

        await apiRequest.request(finalParams, () => submit());

        close();
        refreshPage();
        notifModal.show({
          image: CarWithLady,
          title: create ? locale.accountSuccessRegistered : locale.accountSuccessEdited,
          contentHighlight: [<b>{params.name}</b>],
          content: create ? locale.successfullyRegisteredAccount : locale.successfullyChangeInfoOfAccount,
          secondary: create
            ? {
                text: locale.registerNewAccount,
                onClick: () => {
                  state.show({
                    title: locale.addAccount,
                    actionText: locale.add,
                    create: true
                  });
                }
              }
            : null
        });
      },
      null,
      (err) => {
        if (err.data?.errorCode === 'A0003') {
          makeFormInvalid();
          return applyFieldErrors({
            code: 'Code already exists'
          });
        }
      }
    );
  };

  return (
    <div>
      <NotifModal {...notifModal} />
      <Modal close={loading ? false : close} {...state} className={styles.modalContent}>
        <div className={styles.formContent}>
          <div className={styles.content}>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} sm={6}>
                <Field label={'Category'}>
                  <SelectCategory
                    {...fields.accountCategoryId}
                    onChange={(name, value) => {
                      modifyForm({
                        [name]: {
                          value: value.value,
                          label: value.label
                        },
                        [fields.parentAccountId.name]: {
                          value: null,
                          label: ''
                        },
                        [fields.level.name]: {
                          value: null
                        }
                      });

                      setParentAccount(null);
                    }}
                  />
                </Field>
              </Grid>
              <Grid item xs={6} sm={6}>
                <Field className={styles.status} label={fields.financialStatement.label}>
                  <Autocomplete
                    name="type"
                    value={fields.financialStatement}
                    options={[
                      {
                        label: 'Balance Sheet',
                        value: 'Balance Sheet'
                      },
                      {
                        label: 'Income Statement',
                        value: 'Income Statement'
                      }
                    ]}
                    {...fields.financialStatement}
                    onChange={modifyField}
                  />
                </Field>
              </Grid>
            </Grid>

            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} sm={6}>
                <Field {...fields.level} label={'Level'}>
                  <Autocomplete
                    name="level"
                    value={fields.level.value}
                    options={[
                      {
                        label: '2',
                        value: '2'
                      },
                      {
                        label: '3',
                        value: '3'
                      },
                      {
                        label: '4',
                        value: '4'
                      },
                      {
                        label: '5',
                        value: '5'
                      }
                    ]}
                    {...fields.level}
                    onChange={(name, value) => {
                      if (fields.parentAccountId.value) {
                        getHierarchy(fields.parentAccountId.value);
                      }

                      modifyForm({
                        [name]: {
                          value: value.value
                        },
                        [fields.parentAccountId.name]: {
                          value: null,
                          label: ''
                        }
                      });
                    }}
                  />
                </Field>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Field {...fields.parentAccountId} label={'Parent Account'}>
                  <SelectAccount
                    {...fields.parentAccountId}
                    onChange={(name, value) => {
                      modifyField(name, value);
                      if (value.value) {
                        getHierarchy(value.value);
                      }
                    }}
                    categoryId={fields.accountCategoryId.value}
                    level={fields.level.value ? parseInt(fields.level.value) - 1 : null}
                    disabled={!fields.level.value}
                  />
                </Field>
              </Grid>
            </Grid>

            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} sm={6}>
                <Field {...fields.name}>
                  <TextField {...fields.name} onChange={modifyField} onBlur={validateField} />
                </Field>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field {...fields.code}>
                  <TextField {...fields.code} onChange={modifyField} onBlur={validateField} />
                </Field>
              </Grid>
            </Grid>

            {/* <Grid container direction="row">
              <Grid item xs={12} sm={6}>
                <div className={styles.checkbox}>
                  <Checkbox {...fields.isPostable} onChange={modifyField} checked={fields.isPostable.value} />
                  <Text className={styles.gasText}>Postable</Text>
                </div>
              </Grid>
            </Grid>
            <Grid container direction="row">
              <Grid item xs={12} sm={6}>
                <div className={styles.checkbox}>
                  <Checkbox {...fields.isAffectsCashflow} onChange={modifyField} checked={fields.isAffectsCashflow.value} />
                  <Text className={styles.gasText}>Affects Cashflow Statement</Text>
                </div>
              </Grid>
            </Grid> */}
            <Grid container direction="row">
              <Grid item xs={12} sm={6}>
                <div className={styles.checkbox}>
                  <Checkbox {...fields.isCash} onChange={modifyField} checked={fields.isCash.value} />
                  <Text className={styles.gasText}>Treat as Cash</Text>
                </div>
              </Grid>
            </Grid>

            {fields.accountCategoryId.value && (
              <Grid container direction="row">
                <div className={styles.formFooter}>
                  <Text>Parent Hierarchy (by level):</Text>
                  {accountCategory && (
                    <Text className={styles.category}>
                      <b>
                        Level {accountCategory.level}: {accountCategory.code}
                      </b>{' '}
                      - {accountCategory.name}
                    </Text>
                  )}
                  {parentAccount &&
                    (() => {
                      const flattened = [];

                      let current = parentAccount;
                      while (current) {
                        flattened.push(current);
                        current = current.parentAccount;
                      }

                      const sorted = orderBy(flattened, ['level'], ['asc']); // or 'desc'

                      return (
                        <div className={styles.parentAccount}>
                          {sorted.map((acc) => (
                            <Text key={acc.accountId}>
                              <b>
                                Level {acc.level}: {acc.code}
                              </b>{' '}
                              - {acc.name}
                            </Text>
                          ))}
                        </div>
                      );
                    })()}
                  {nextLevelParentAccount && (
                    <Text className={styles.category}>
                      <b>
                        Level {nextLevelParentAccount.level}: {nextLevelParentAccount.code}
                      </b>{' '}
                      - {nextLevelParentAccount.name}
                    </Text>
                  )}
                </div>
              </Grid>
            )}
          </div>
        </div>
        <ActionButton
          right
          items={[
            {
              onClick: () => {
                state.close();
              },
              disabled: loading,
              text: locale.cancel
            },
            {
              onClick: () => {
                submitForm(submit);
              },
              disabled: !isFormSubmittable,
              loading,
              text: actionText
            }
          ]}
        />
      </Modal>
    </div>
  );
};

export default AccountForm;
